import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseStatusViewComponent } from '../base-status-view.component';
import { Kontrolltermin, KontrollterminService } from '../../../services/kontrolltermin.service';
import { MitarbeiterService } from '../../../services/mitarbeiter.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { AdminPermissionService } from '../../../services/admin-permission.service';

@Component({
  selector: 'app-geplant-view',
  templateUrl: './geplant-view.component.html',
  styleUrls: ['./geplant-view.component.scss']
})
export class GeplantViewComponent extends BaseStatusViewComponent implements OnInit {
  selectedTechnikerId: string = '';
  technikerOptions: any[] = [];
  override isLoading = false;
  override isSaving = false;
  error: string | null = null;

  constructor(
    private mitarbeiterService: MitarbeiterService,
    private kontrollterminService: KontrollterminService,
    private adminPermissionService: AdminPermissionService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router,
    formBuilder: FormBuilder
  ) {
    super(formBuilder);
  }
  
  override ngOnInit(): void {
    super.ngOnInit();
    this.loadTechnikerOptions();
  }
  
  getStatusTitle(): string {
    return 'Geplanter Kontrolltermin';
  }
  
  getAvailableActions(): string[] {
    return ['Aktivieren']; // Only Aktivieren action
  }
  
  getFormFields(): any[] {
    return [
      { field: 'techniker_id', label: 'Techniker', type: 'select' }
    ];
  }
  
  onSave(): void {
    this.logAction('Save');
    console.log('📅 GEPLANT: Save action triggered');
    
    if (!this.selectedTechnikerId) {
      this.snackBar.open('Bitte wählen Sie einen Techniker aus', 'Schließen', { duration: 3000 });
      return;
    }
    
    this.isSaving = true;
    
    // TODO: Call API to save techniker assignment
    // For now, simulate API call and update the kontrolltermin object
    setTimeout(() => {
      // Update the kontrolltermin object with the new techniker
      if (this.kontrolltermin) {
        this.kontrolltermin.techniker_id = this.selectedTechnikerId;
        console.log('📅 GEPLANT: Updated kontrolltermin.techniker_id to:', this.selectedTechnikerId);
      }
      
      this.isSaving = false;
      this.snackBar.open('Techniker wurde zugewiesen', 'Schließen', { duration: 3000 });
    }, 1000);
  }
  
  onCancel(): void {
    this.logAction('Cancel');
    console.log('📅 GEPLANT: Cancel action triggered');
    this.router.navigate(['/kontrolltermine']);
  }
  
  onStatusChange(newStatus: string): void {
    this.logAction(`Status change to ${newStatus}`);
    console.log('📅 GEPLANT: Status change to', newStatus);
    this.emitStatusChange(newStatus);
  }
  
  onHeaderBatchChanged(changes: { [key: string]: any }): void {
    console.log('GeplantViewComponent: onHeaderBatchChanged() called with:', changes);
    
    if (!this.kontrolltermin?.id) {
      this.snackBar.open('Kontrolltermin ID nicht gefunden', 'Schließen', { duration: 3000 });
      return;
    }

    if (Object.keys(changes).length === 0) return;

    this.isSaving = true;

    this.kontrollterminService.updateKontrolltermin(this.kontrolltermin.id, changes).subscribe({
      next: (updatedKontrolltermin) => {
        console.log('GeplantViewComponent: Update successful:', updatedKontrolltermin);
        this.snackBar.open('Änderungen erfolgreich gespeichert', 'Schließen', { duration: 3000 });
        
        // Update local state
        if (changes['techniker_id']) {
          this.selectedTechnikerId = changes['techniker_id'];
          if (this.kontrolltermin) {
            this.kontrolltermin.techniker_id = changes['techniker_id'];
            if (updatedKontrolltermin.techniker) {
              this.kontrolltermin.techniker = updatedKontrolltermin.techniker;
            }
          }
        }
        
        this.isSaving = false;
      },
      error: (error) => {
        console.error('GeplantViewComponent: Error updating:', error);
        this.snackBar.open('Fehler beim Speichern der Änderungen', 'Schließen', { duration: 3000 });
        this.isSaving = false;
      }
    });
  }
  
  // Geplant-specific methods
  loadTechnikerOptions(): void {
    console.log('=== LOAD TECHNIKER OPTIONS ===');
    this.isLoading = true;
    
    this.mitarbeiterService.getTechnicians().subscribe({
      next: (response) => {
        this.technikerOptions = response.data || [];
        console.log('Loaded techniker options:', this.technikerOptions);
        this.isLoading = false;
        this.populateForm();
      },
      error: (error) => {
        console.error('Error loading techniker:', error);
        this.snackBar.open('Fehler beim Laden der Techniker', 'Schließen', { duration: 3000 });
        this.isLoading = false;
        this.populateForm();
      }
    });
  }

  private populateForm(): void {
    const kt = this.kontrolltermin;
    
    console.log('=== POPULATE FORM ===');
    console.log('Kontrolltermin data:', kt);
    console.log('kt.techniker_id:', kt?.techniker_id, typeof kt?.techniker_id);
    console.log('Available techniker options:', this.technikerOptions);
    
    if (kt?.techniker_id && this.technikerOptions.length > 0) {
      // Handle data type mismatch: kontrolltermin has string ID, techniker options have numeric ID
      const numericId = parseInt(kt.techniker_id, 10);
      
      console.log('Looking for techniker with ID:', kt.techniker_id, 'converted to:', numericId);
      
      const foundTechniker = this.technikerOptions.find(t => 
        t.id === numericId || 
        t.id === kt.techniker_id ||
        t.id === kt.techniker_id?.toString()
      );
      
      if (foundTechniker) {
        this.selectedTechnikerId = foundTechniker.id.toString();
        console.log('Found matching techniker for form:', foundTechniker);
      } else {
        console.log('No matching techniker found for ID:', kt.techniker_id);
        this.selectedTechnikerId = kt.techniker_id;
      }
    }
    
    console.log('Set selectedTechnikerId:', this.selectedTechnikerId, typeof this.selectedTechnikerId);
  }

  getCurrentTechnikerName(): string {
    if (!this.kontrolltermin?.techniker_id) {
      return 'Nicht zugewiesen';
    }
    
    const technikerId = this.kontrolltermin.techniker_id;
    const numericId = parseInt(technikerId, 10);
    
    const techniker = this.technikerOptions.find(t => 
      t.id === numericId || 
      t.id === technikerId ||
      t.id === technikerId.toString()
    );
    
    return techniker ? techniker.display_name : 'Unbekannt';
  }
  
  activateKontrolltermin(): void {
    this.logAction('Activate');
    console.log('📅 GEPLANT: Activate action triggered');
    console.log('📊 Selected Techniker ID:', this.selectedTechnikerId);
    
    if (!this.selectedTechnikerId) {
      this.snackBar.open('Bitte wählen Sie einen Techniker aus', 'Schließen', { duration: 3000 });
      return;
    }
    
    // Show confirmation dialog
    this.showActivationConfirmation();
  }

  private showActivationConfirmation(): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Auftrag aktivieren',
        message: 'Möchten Sie diesen Auftrag aktivieren? Alle Indikatoren werden für die Bearbeitung vorbereitet.',
        confirmText: 'Aktivieren',
        cancelText: 'Abbrechen',
        confirmColor: 'primary'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.processJobActivation();
      }
    });
  }

  private processJobActivation(): void {
    if (!this.kontrolltermin?.id) {
      this.snackBar.open('Kein Kontrolltermin gefunden', 'Schließen', { duration: 3000 });
      return;
    }

    this.isSaving = true;
    this.error = null;

    console.log('📅 GEPLANT: Processing job activation for ID:', this.kontrolltermin.id);
    console.log('📅 GEPLANT: Kontrolltermin details:', {
      id: this.kontrolltermin.id,
      status_id: this.kontrolltermin.status_id,
      standort_id: this.kontrolltermin.standort_id,
      techniker_id: this.kontrolltermin.techniker_id,
      datum_durchfuerung: this.kontrolltermin.datum_durchfuerung,
      einsatznummer: this.kontrolltermin.einsatznummer
    });

    // Check current status
    const statusNames = {
      1: 'Gelöscht',
      2: 'Abgebrochen', 
      3: 'Fehlerhaft',
      4: 'Geplant',
      5: 'Aktuell',
      6: 'In Bearbeitung',
      7: 'Abgeschlossen',
      8: 'Freigegeben'
    };
    const currentStatusName = statusNames[this.kontrolltermin.status_id as keyof typeof statusNames] || 'Unknown';
    console.log(`📅 GEPLANT: Current status: ${this.kontrolltermin.status_id} (${currentStatusName})`);

    // Check if current user is admin
    const isAdmin = this.adminPermissionService.isAdmin();
    console.log('📅 GEPLANT: User admin status:', isAdmin);
    console.log('📅 GEPLANT: Sending accept request with isAdmin:', isAdmin, 'forceActivation:', isAdmin);

    this.kontrollterminService.acceptKontrolltermin(this.kontrolltermin.id, { 
      isAdmin: isAdmin, 
      forceActivation: isAdmin 
    }).subscribe({
      next: (result) => {
        console.log('📅 GEPLANT: Activation successful:', result);
        this.isSaving = false;
        
        if (result && result.success) {
          // Show success message
          this.snackBar.open('Auftrag erfolgreich aktiviert', 'Schließen', { duration: 3000 });
          
          // Emit status change to parent component
          this.emitStatusChange('aktuell');
          
          // Navigate back to kontrolltermine list
          setTimeout(() => {
            this.router.navigate(['/kontrolltermine']);
          }, 1500);
        } else {
          this.snackBar.open(result?.message || 'Fehler beim Aktivieren des Auftrags', 'Schließen', { duration: 5000 });
        }
      },
      error: (error) => {
        console.error('📅 GEPLANT: Activation error:', error);
        this.isSaving = false;
        this.snackBar.open('Fehler beim Aktivieren des Auftrags', 'Schließen', { duration: 5000 });
      }
    });
  }

  /**
   * Delete kontrolltermin (mark as gelöscht)
   */
  onDelete(): void {
    if (!this.kontrolltermin) return;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Kontrolltermin löschen',
        message: `Möchten Sie den Kontrolltermin #${this.kontrolltermin.einsatznummer} wirklich löschen?`,
        confirmText: 'Löschen',
        cancelText: 'Abbrechen',
        confirmColor: 'warn'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.performDelete();
      }
    });
  }

  /**
   * Perform the actual delete operation
   */
  private performDelete(): void {
    if (!this.kontrolltermin) return;

    this.isSaving = true;
    
    // Update status to "gelöscht" (status_id: 1)
    const updateData = {
      status_id: 1 // gelöscht status
    };

    this.kontrollterminService.updateKontrolltermin(this.kontrolltermin.id, updateData).subscribe({
      next: (response) => {
        this.snackBar.open('Kontrolltermin wurde gelöscht', 'Schließen', { 
          duration: 3000,
          panelClass: ['success-snackbar']
        });
        
        // Navigate back to the list
        this.router.navigate(['/kontrolltermine']);
      },
      error: (error) => {
        console.error('Error deleting kontrolltermin:', error);
        this.snackBar.open('Fehler beim Löschen des Kontrolltermins', 'Schließen', { 
          duration: 3000,
          panelClass: ['error-snackbar']
        });
        this.isSaving = false;
      }
    });
  }
}
